import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { customerService } from '../../services/api';
import {
	ArrowLeftIcon,
	CurrencyDollarIcon,
	ShoppingBagIcon,
	CalendarIcon,
	TruckIcon,
	ChartBarIcon,
	UserIcon,
	StarIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const CustomerAnalytics = () => {
	const { customerId } = useParams();
	const [customer, setCustomer] = useState(null);
	const [analytics, setAnalytics] = useState(null);
	const [lifetime, setLifetime] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const loadCustomerData = async () => {
			try {
				setLoading(true);
				const [customerData, analyticsData, lifetimeData] = await Promise.all([
					                                                                      customerService.getCustomer(customerId),
					                                                                      customerService.getCustomerAnalytics(customerId),
					                                                                      customerService.getCustomerLifetime(customerId)
				                                                                      ]);

				setCustomer(customerData.data);
				setAnalytics(analyticsData.data);
				setLifetime(lifetimeData.data);
				setError(null);
			} catch (err) {
				console.error('Error loading customer data:', err);
				setError('Failed to load customer analytics');
				toast.error('Failed to load customer analytics');
			} finally {
				setLoading(false);
			}
		};

		if (customerId) {
			loadCustomerData();
		}
	}, [customerId]);

	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD'
		}).format(amount || 0);
	};

	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		return format(new Date(dateString), 'MMM d, yyyy');
	};

	const getSegmentBadgeColor = (segment) => {
		switch (segment?.toLowerCase()) {
			case 'vip customer':
				return 'bg-purple-100 text-purple-800 border-purple-200';
			case 'loyal customer':
				return 'bg-green-100 text-green-800 border-green-200';
			case 'regular customer':
				return 'bg-blue-100 text-blue-800 border-blue-200';
			case 'at risk customer':
				return 'bg-yellow-100 text-yellow-800 border-yellow-200';
			case 'lost customer':
				return 'bg-red-100 text-red-800 border-red-200';
			case 'new customer':
				return 'bg-gray-100 text-gray-800 border-gray-200';
			default:
				return 'bg-gray-100 text-gray-800 border-gray-200';
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
				<p className="font-bold">Error</p>
				<p>{error}</p>
			</div>
		);
	}

	if (!customer) {
		return (
			<div className="text-center py-12">
				<UserIcon className="mx-auto h-12 w-12 text-gray-400" />
				<h3 className="mt-2 text-sm font-medium text-gray-900">Customer not found</h3>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center space-x-4">
					<Link
						to="/customers"
						className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
					>
						<ArrowLeftIcon className="h-4 w-4 mr-1" />
						Back to Customers
					</Link>
				</div>
			</div>

			{/* Customer Header */}
			<div className="bg-white shadow rounded-lg p-6">
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-4">
						<div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
							<UserIcon className="h-8 w-8 text-gray-600" />
						</div>
						<div>
							<h1 className="text-2xl font-bold text-gray-900">
								{customer.customer_info?.customer_name || 'Unknown Customer'}
							</h1>
							<p className="text-gray-600">{customer.customer_info?.customer_email}</p>
							{customer.customer_info?.customer_username && (
								<p className="text-sm text-gray-500">@{customer.customer_info.customer_username}</p>
							)}
						</div>
					</div>
					{lifetime?.customer_segment && (
						<div className={`px-3 py-1 rounded-full border text-sm font-medium ${getSegmentBadgeColor(lifetime.customer_segment)}`}>
							{lifetime.customer_segment}
						</div>
					)}
				</div>
			</div>

			{/* Key Metrics */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
				<div className="bg-white overflow-hidden shadow rounded-lg">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<CurrencyDollarIcon className="h-6 w-6 text-green-400" />
							</div>
							<div className="ml-5 w-0 flex-1">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate">
										Lifetime Value
									</dt>
									<dd className="text-lg font-medium text-gray-900">
										{formatCurrency(lifetime?.lifetime_value)}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>

				<div className="bg-white overflow-hidden shadow rounded-lg">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<ShoppingBagIcon className="h-6 w-6 text-blue-400" />
							</div>
							<div className="ml-5 w-0 flex-1">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate">
										Total Orders
									</dt>
									<dd className="text-lg font-medium text-gray-900">
										{customer.statistics?.total_orders || 0}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>

				<div className="bg-white overflow-hidden shadow rounded-lg">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<ChartBarIcon className="h-6 w-6 text-purple-400" />
							</div>
							<div className="ml-5 w-0 flex-1">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate">
										Average Order
									</dt>
									<dd className="text-lg font-medium text-gray-900">
										{formatCurrency(customer.statistics?.average_order_value)}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>

				<div className="bg-white overflow-hidden shadow rounded-lg">
					<div className="p-5">
						<div className="flex items-center">
							<div className="flex-shrink-0">
								<CalendarIcon className="h-6 w-6 text-orange-400" />
							</div>
							<div className="ml-5 w-0 flex-1">
								<dl>
									<dt className="text-sm font-medium text-gray-500 truncate">
										Days Since Last Order
									</dt>
									<dd className="text-lg font-medium text-gray-900">
										{lifetime?.days_since_last_order || 'N/A'}
									</dd>
								</dl>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Customer Journey */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Customer Timeline */}
				<div className="bg-white shadow rounded-lg p-6">
					<h3 className="text-lg font-medium text-gray-900 mb-4">Customer Journey</h3>
					<div className="space-y-4">
						<div className="flex items-center justify-between py-2 border-b border-gray-200">
							<span className="text-sm text-gray-600">First Order</span>
							<span className="text-sm font-medium">{formatDate(lifetime?.first_order_date)}</span>
						</div>
						<div className="flex items-center justify-between py-2 border-b border-gray-200">
							<span className="text-sm text-gray-600">Last Order</span>
							<span className="text-sm font-medium">{formatDate(lifetime?.last_order_date)}</span>
						</div>
						<div className="flex items-center justify-between py-2 border-b border-gray-200">
							<span className="text-sm text-gray-600">Customer Tenure</span>
							<span className="text-sm font-medium">{lifetime?.tenure_months} months</span>
						</div>
						<div className="flex items-center justify-between py-2 border-b border-gray-200">
							<span className="text-sm text-gray-600">Order Frequency</span>
							<span className="text-sm font-medium">{lifetime?.frequency_score} orders/month</span>
						</div>
						<div className="flex items-center justify-between py-2">
							<span className="text-sm text-gray-600">Total Tax Paid</span>
							<span className="text-sm font-medium">{formatCurrency(lifetime?.total_tax_paid)}</span>
						</div>
					</div>
				</div>

				{/* Order Status Breakdown */}
				<div className="bg-white shadow rounded-lg p-6">
					<h3 className="text-lg font-medium text-gray-900 mb-4">Order Status Breakdown</h3>
					<div className="space-y-3">
						{customer.status_breakdown && Object.entries(customer.status_breakdown).map(([status, count]) => (
							<div key={status} className="flex items-center justify-between">
								<div className="flex items-center">
									<div className={`w-3 h-3 rounded-full mr-3 ${
										status === 'pending' ? 'bg-yellow-400' :
										status === 'processing' ? 'bg-blue-400' :
										status === 'fulfilled' ? 'bg-green-400' :
										status === 'cancelled' ? 'bg-red-400' : 'bg-gray-400'
									}`}></div>
									<span className="text-sm text-gray-600 capitalize">{status}</span>
								</div>
								<span className="text-sm font-medium">{count}</span>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Monthly Spending & Top Products */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Monthly Spending */}
				<div className="bg-white shadow rounded-lg p-6">
					<h3 className="text-lg font-medium text-gray-900 mb-4">Monthly Spending (Last 12 Months)</h3>
					{analytics?.monthly_spending && analytics.monthly_spending.length > 0 ? (
						<div className="space-y-3">
							{analytics.monthly_spending.map((month, index) => (
								<div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                                    <span className="text-sm text-gray-600">
                                        {new Date(month.year, month.month - 1).toLocaleDateString('en-US', {
	                                        year: 'numeric',
	                                        month: 'long'
                                        })}
                                    </span>
									<div className="text-right">
										<div className="text-sm font-medium">{formatCurrency(month.total_spent)}</div>
										<div className="text-xs text-gray-500">{month.order_count} orders</div>
									</div>
								</div>
							))}
						</div>
					) : (
						 <p className="text-sm text-gray-500">No spending data available</p>
					 )}
				</div>

				{/* Top Products */}
				<div className="bg-white shadow rounded-lg p-6">
					<h3 className="text-lg font-medium text-gray-900 mb-4">Most Ordered Products</h3>
					{analytics?.top_products && analytics.top_products.length > 0 ? (
						<div className="space-y-3">
							{analytics.top_products.slice(0, 5).map((product, index) => (
								<div key={product.id} className="flex items-center justify-between py-2 border-b border-gray-100">
									<div className="flex-1">
										<div className="text-sm font-medium text-gray-900">{product.name}</div>
										<div className="text-xs text-gray-500">SKU: {product.sku}</div>
									</div>
									<div className="text-right">
										<div className="text-sm font-medium">{product.total_quantity} units</div>
										<div className="text-xs text-gray-500">{product.order_frequency} orders</div>
									</div>
								</div>
							))}
						</div>
					) : (
						 <p className="text-sm text-gray-500">No product data available</p>
					 )}
				</div>
			</div>

			{/* Shipping Preferences */}
			{analytics?.shipping_preferences && analytics.shipping_preferences.length > 0 && (
				<div className="bg-white shadow rounded-lg p-6">
					<h3 className="text-lg font-medium text-gray-900 mb-4">Shipping Preferences</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{analytics.shipping_preferences.map((preference, index) => (
							<div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
								<div className="flex items-center">
									<TruckIcon className="h-5 w-5 text-gray-400 mr-2" />
									<span className="text-sm text-gray-700">{preference.requested_shipping_service}</span>
								</div>
								<span className="text-sm font-medium text-gray-900">{preference.usage_count}</span>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Recent Orders */}
			<div className="bg-white shadow rounded-lg p-6">
				<div className="flex items-center justify-between mb-4">
					<h3 className="text-lg font-medium text-gray-900">Recent Orders</h3>
					<Link
						to={`/customers/${customerId}/orders`}
						className="text-sm text-primary-600 hover:text-primary-500"
					>
						View All Orders →
					</Link>
				</div>
				{customer.recent_orders && customer.recent_orders.length > 0 ? (
					<div className="overflow-hidden">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
							<tr>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Order
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Date
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Status
								</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Total
								</th>
							</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
							{customer.recent_orders.slice(0, 5).map((order) => (
								<tr key={order.id}>
									<td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
										{order.order_number}
									</td>
									<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
										{formatDate(order.order_date)}
									</td>
									<td className="px-4 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
	                                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
	                                            order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
	                                            order.status === 'fulfilled' ? 'bg-green-100 text-green-800' :
	                                            order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
	                                            'bg-gray-100 text-gray-800'
                                            }`}>
                                                {order.status}
                                            </span>
									</td>
									<td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
										{formatCurrency(order.order_total)}
									</td>
								</tr>
							))}
							</tbody>
						</table>
					</div>
				) : (
					 <p className="text-sm text-gray-500">No recent orders found</p>
				 )}
			</div>
		</div>
	);
};

export default CustomerAnalytics;
